import { Text } from "~/components/Text"
import { useCallback, useEffect, useState } from "react"
import { WechatFilled } from "@ant-design/icons"
import { Button, Flex, Modal, QRCode, Result, Spin, Typography } from "antd"
import { OrderStatus } from "~/common/constants"
import { formatPrice, isAbortError } from "~/common/utils"
import { createOrReusePayment, getPaymentStatus } from "~/api/payment"
import styles from "./index.css"

const { Title } = Typography

const pollingIntervalMs = 1_000

const getErrorMessage = (error: unknown) => {
  return error instanceof Error && error.message
    ? error.message
    : "支付二维码生成失败，请稍后重试"
}

const getRemainingSeconds = (expiresAt: number) => {
  return Math.max(0, Math.ceil(expiresAt - Date.now() / 1_000))
}

const formatCountdown = (seconds: number) => {
  const hours = Math.floor(seconds / 3_600)
  const minutes = Math.floor((seconds % 3_600) / 60)
  const remainingSeconds = seconds % 60

  return [hours, minutes, remainingSeconds]
    .map((item) => String(item).padStart(2, "0"))
    .join(" : ")
}

type PaymentModalProps = {
  intent: API.Payment.CreateOrReusePaymentParams
  open: boolean
  originalAmount: number
  policyUrl: string
  successDescription: string
  successTitle: string
  onCancel: () => void
  onComplete: () => void
  onGoHome: () => void
  onPaid: () => Promise<void>
}

export const PaymentModal = ({
  intent,
  open,
  originalAmount,
  policyUrl,
  successDescription,
  successTitle,
  onCancel,
  onComplete,
  onGoHome,
  onPaid,
}: PaymentModalProps) => {
  const [attempt, setAttempt] = useState(0)
  const [payment, setPayment] = useState<API.Payment.PaymentReadyResponse>()
  const [paidPayment, setPaidPayment] =
    useState<API.Payment.PaymentAlreadyPaidResponse>()
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [creating, setCreating] = useState(true)
  const [createError, setCreateError] = useState<string>()
  const [statusError, setStatusError] = useState<string>()
  const [pollAttempt, setPollAttempt] = useState(0)
  const [terminalExpired, setTerminalExpired] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [successCloseSeconds, setSuccessCloseSeconds] = useState(3)

  const retryCreate = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  useEffect(() => {
    if (!open) return

    const controller = new AbortController()

    setCreating(true)
    setCreateError(undefined)
    setStatusError(undefined)
    setPayment(undefined)
    setPaidPayment(undefined)
    setTerminalExpired(false)
    setSucceeded(false)

    void createOrReusePayment(intent, controller.signal)
      .then(async (response) => {
        if (!response.data) {
          throw new Error("支付信息为空，请稍后重试")
        }

        if (response.data.result === "already_paid") {
          setPaidPayment(response.data)
          await onPaid()
          if (!controller.signal.aborted) {
            setSucceeded(true)
          }
          return
        }

        setPayment(response.data)
        setRemainingSeconds(getRemainingSeconds(response.data.expires_at))
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return
        setCreateError(getErrorMessage(error))
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setCreating(false)
        }
      })

    return () => controller.abort()
  }, [attempt, intent, onPaid, open])

  useEffect(() => {
    if (!payment) return

    const updateCountdown = () => {
      setRemainingSeconds(getRemainingSeconds(payment.expires_at))
    }

    updateCountdown()
    const timer = window.setInterval(updateCountdown, 1_000)

    return () => window.clearInterval(timer)
  }, [payment])

  useEffect(() => {
    if (!payment || getRemainingSeconds(payment.expires_at) <= 0) return

    const controller = new AbortController()
    let timer: number | undefined
    let stopped = false

    setStatusError(undefined)

    const poll = async () => {
      if (stopped || getRemainingSeconds(payment.expires_at) <= 0) return

      try {
        const response = await getPaymentStatus(
          { order_no: payment.order_no },
          controller.signal,
        )
        const status = response.data?.order_status

        switch (status) {
          case OrderStatus.succeeded:
            stopped = true
            await onPaid()
            if (!controller.signal.aborted) {
              setSucceeded(true)
            }
            return
          case OrderStatus.closed:
          case OrderStatus.cancelled:
            stopped = true
            setTerminalExpired(true)
            return
        }

        timer = window.setTimeout(poll, pollingIntervalMs)
      } catch (error) {
        if (!isAbortError(error)) {
          stopped = true
          setStatusError("支付状态查询失败，请重新查询")
        }
      }
    }

    timer = window.setTimeout(poll, pollingIntervalMs)

    return () => {
      stopped = true
      controller.abort()
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [onPaid, payment, pollAttempt])

  useEffect(() => {
    if (!succeeded) return

    setSuccessCloseSeconds(3)
    const interval = window.setInterval(() => {
      setSuccessCloseSeconds((value) => Math.max(0, value - 1))
    }, 1_000)
    const timeout = window.setTimeout(onComplete, 3_000)

    return () => {
      window.clearInterval(interval)
      window.clearTimeout(timeout)
    }
  }, [onComplete, succeeded])

  const expired = terminalExpired || remainingSeconds <= 0
  const successfulPayment = paidPayment ?? payment

  return (
    <Modal
      centered
      className={styles.paymentModal}
      destroyOnHidden
      footer={null}
      open={open}
      title={succeeded ? undefined : payment?.description || "微信支付"}
      width={480}
      onCancel={onCancel}
    >
      {succeeded ? (
        <Result
          extra={[
            <Button
              key="continue"
              onClick={onComplete}
              type="primary"
            >
              继续创作
            </Button>,
            <Button
              key="home"
              onClick={onGoHome}
            >
              返回首页
            </Button>,
          ]}
          status="success"
          subTitle={successDescription}
          title={successTitle}
        >
          <Flex
            className={styles.paymentSuccessDetails}
            gap={10}
            vertical
          >
            {successfulPayment ? (
              <>
                <Flex justify="space-between">
                  <Text type="secondary">购买内容</Text>
                  <Text strong>{successfulPayment.description}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text type="secondary">支付金额</Text>
                  <Text strong>
                    ¥{formatPrice(successfulPayment.amount / 100)}
                  </Text>
                </Flex>
              </>
            ) : null}
            <Text
              className={styles.paymentSuccessAutoClose}
              type="secondary"
            >
              {successCloseSeconds} 秒后自动关闭
            </Text>
          </Flex>
        </Result>
      ) : creating ? (
        <Flex
          align="center"
          className={styles.paymentLoading}
          gap={16}
          justify="center"
          vertical
        >
          <Spin size="large" />
          <Text type="secondary">正在生成支付二维码...</Text>
        </Flex>
      ) : createError || !payment ? (
        <Result
          status="error"
          subTitle={createError}
          title="支付二维码生成失败"
          extra={
            <Button
              onClick={retryCreate}
              type="primary"
            >
              重新获取
            </Button>
          }
        />
      ) : (
        <Flex
          align="center"
          className={styles.paymentContent}
          gap={18}
          vertical
        >
          <Flex
            align="center"
            gap={8}
          >
            <WechatFilled className={styles.wechatIcon} />
            <Text
              size={15}
              className={styles.paymentCountdown}
            >
              请在 {formatCountdown(remainingSeconds)} 内扫码完成支付
            </Text>
          </Flex>
          <Flex
            align="baseline"
            gap={10}
          >
            <Title
              className={styles.paymentAmount}
              level={2}
            >
              ¥{formatPrice(payment.amount / 100)}
            </Title>
            {originalAmount > payment.amount ? (
              <Text
                size={15}
                delete
                type="secondary"
              >
                ¥{formatPrice(originalAmount / 100)}
              </Text>
            ) : null}
          </Flex>
          <QRCode
            bgColor="#fff"
            bordered={false}
            color="#000"
            errorLevel="M"
            marginSize={2}
            onRefresh={retryCreate}
            size={216}
            status={expired ? "expired" : "active"}
            type="svg"
            value={payment.payment_url}
          />
          {statusError ? (
            <Button onClick={() => setPollAttempt((value) => value + 1)}>
              {statusError}
            </Button>
          ) : (
            <Text type="secondary">
              {expired
                ? "二维码已过期，请重新获取"
                : "请使用微信扫一扫完成支付"}
            </Text>
          )}
          <Flex
            align="center"
            className={styles.paymentPolicy}
            gap={2}
            justify="center"
          >
            <Text type="secondary">开通即同意</Text>
            <Button
              className={styles.paymentPolicyLink}
              color="primary"
              href={policyUrl}
              rel="noreferrer"
              target="_blank"
              variant="link"
            >
              《咔嗒爆用户协议》
            </Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}
